import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import DataTable from '../../reusable/DataTable/DataTable';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import SegmentedTabs from '../../reusable/SegmentedTabs/SegmentedTabs';
import Button from '../../reusable/Button/Button';
import CopyButton from '../../reusable/CopyButton/CopyButton';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import LineChart from '../../reusable/LineChart/LineChart';
import { formatAxisDate, formatCurrency } from '../../reusable/LineChart/formatters';
import { CHART_COLORS } from '../../reusable/charts/chartTheme';
import DashboardSlider from '../../reusable/DashboardSlider/DashboardSlider';
import ShareDialog from '../../layout/ShareDialog';
import { ChartSkeleton, SummarySkeleton } from './DashboardSkeleton';
import {
  useGetAccountsQuery,
  useGetDashboardChartQuery,
  useGetDashboardStatsQuery,
} from '../../store/api/dashboardApi';
import { authErrorMessage } from '../../store/api/authHelpers';
import { debounce } from '../../utilities/debounce';
import { NA, phaseLabel, usd } from '../../utilities/format';
import {
  accountStateLabel,
  formatAccountSize,
  formatLoginId,
  formatPlanName,
} from '../../utilities/accounts';
import { selectAccount, selectPickedAccount } from '../../store/slices/accountSlice';
import { t } from '../../i18n/i18n';
import './Dashboard.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * Rows per page once the card is expanded — 10 × 39px fits without clipping,
 * and it is also the request's `page_size`: the endpoint pages server-side, so
 * a page is one fetch rather than a slice of everything held in memory.
 */
const ROWS_PER_PAGE = 10;
/** Offered in the pager. 218 accounts is 22 pages at ten a time. */
const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50];

/** Rows the card shows before "View all" expands it. */
const COLLAPSED_ROWS = 5;

/** Every keystroke would be a request, so the box settles first. */
const SEARCH_DELAY = 300;

const ACCOUNT_FILTERS = [
  { label: 'All Accounts', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

// The plans Alpha sells (TI review E2) — the same names the Account Type
// column shows. The pick is sent as `account_type`, which the endpoint filters
// on by plan.
const ACCOUNT_TYPE_FILTERS = [
  { label: 'All Types', value: 'all' },
  { label: 'Alpha Direct', value: 'Alpha Direct' },
  { label: 'Alpha Pro 6%', value: 'Alpha Pro 6%' },
  { label: 'Alpha Pro 8%', value: 'Alpha Pro 8%' },
  { label: 'Alpha Pro 10%', value: 'Alpha Pro 10%' },
  { label: 'Alpha One 6%', value: 'Alpha One 6%' },
  { label: 'Alpha One 10%', value: 'Alpha One 10%' },
  { label: 'Alpha One 12%', value: 'Alpha One 12%' },
  { label: 'Alpha Swing', value: 'Alpha Swing' },
  { label: 'Alpha Three', value: 'Alpha Three' },
];

const ACCOUNT_SIZE_FILTERS = [
  { label: 'All Sizes', value: 'all' },
  { label: '$5K', value: 5000 },
  { label: '$10K', value: 10000 },
  { label: '$25K', value: 25000 },
  { label: '$50K', value: 50000 },
  { label: '$100K', value: 100000 },
  { label: '$200K', value: 200000 },
];

/** Filter value → the endpoint's `is_Active`. "all" omits the param. */
const IS_ACTIVE = { all: undefined, active: true, inactive: false };

/** The delta beside the headline figure carries a sign but no $ — 1:8148. */
const signedNumber = (value) =>
  `${value < 0 ? '-' : '+'}${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Series tab → the response's value array, and the colour it draws in. */
const SERIES = {
  Profit: { key: "profit", color: CHART_COLORS.up },
  Balance: { key: "balance", color: CHART_COLORS.info },
};

/**
 * A change as a percentage: "+1.24%". A real move too small for two decimals
 * reads "<0.01%" (the delta's colour and signed amount carry the direction),
 * so a loss never shows as "+0.00%" (TI review N99).
 */
const formatPct = (pct) => {
  if (pct === 0) return '0.00%';
  if (Math.abs(pct) < 0.01) return '<0.01%';
  return `${pct > 0 ? '+' : '-'}${Math.abs(pct).toFixed(2)}%`;
};

/** The response's `{ time, value }[]` per series → LineChart's `{ x, y }[]`. */
const toSeries = (chart, series) => {
  const rows = chart?.[SERIES[series].key] ?? [];
  return rows
    .map((row) => {
      const y = Number(row?.value);
      // "2026-07-24 00:00:00" → ISO, which Safari also parses.
      const x = String(row?.time ?? "").replace(" ", "T");
      return Number.isFinite(y) && Number.isFinite(Date.parse(x))
        ? { x, y }
        : null;
    })
    .filter(Boolean);
};

const CLOCK = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
/** 1D comes back hourly, where every date tick would read the same. */
const isIntraday = (points) =>
  points.length > 1 &&
  Date.parse(points[points.length - 1].x) - Date.parse(points[0].x) <=
  48 * 3600000;

/** NEEDS CONFIRMING: 0 is read as "today", not "none scheduled". */
const nextPayout = (days) => {
  // Before Number(), which turns null into 0.
  if (days == null) return NA;
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return NA;
  if (n === 0) return t("next payout today");
  return n === 1 ? t("next in {{n}} day", { n }) : t("next in {{n}} days", { n });
};

/** `resets_in_seconds` → "resets in 18h 35m". Two units at most, no ticking. */
const resetsIn = (seconds) => {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return NA;
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days) return t("resets in {{days}}d {{hours}}h", { days, hours });
  if (hours) return t("resets in {{hours}}h {{minutes}}m", { hours, minutes });
  // "0m" would read as expired.
  return t("resets in {{minutes}}m", { minutes: Math.max(minutes, 1) });
};

/** "31 Aug 2026", the format the Start Date column draws. */
const startDate = (value) => {
  const parsed = Date.parse(value ?? "");
  return Number.isFinite(parsed) ? format(parsed, "dd MMM yyyy") : NA;
};

/** "$4,120 / $5,000" over a progress rail — 1:8063. */
const Progress = ({ used, total, tone }) => {
  if (used == null || total == null) {
    return (
      <div className="dash-progress is-na">
        <span className="dash-progress__label">{NA}</span>
        <span className="dash-progress__rail" />
      </div>
    );
  }

  return (
    <div className="dash-progress">
      <span className="dash-progress__label">
        {usd(used)} / {usd(total)}
      </span>
      <span className="dash-progress__rail">
        <span
          className={`dash-progress__fill is-${tone}`}
          // A 0 total would make this NaN%.
          style={{
            width: `${total > 0 ? Math.min((used / total) * 100, 100) : 0}%`,
          }}
        />
      </span>
    </div>
  );
};

/**
 * The narrowest a chip column may get: the shared chip width in Dashboard.scss
 * (96px — "Disqualified" at the danger size, the longest label either column
 * shows) plus the cell's 12px padding each side.
 */
const CHIP_CELL_MIN = 120;

/** The Figma table (1:8032). Built in the component because Open navigates. */
const buildColumns = (openAccount) => [
  {
    key: "login_id",
    header: "Login ID",
    width: 12,
    render: (r) => (
      <span className="dash-login">
        <span className="dash-login__id">{formatLoginId(r.login_id)}</span>
        <CopyButton value={r.login_id} label={t("Login ID")} />
      </span>
    ),
  },
  {
    key: "daily_headroom",
    header: "Daily Loss",
    width: 18,
    render: (r) => (
      <Progress
        used={r.daily_headroom?.used}
        total={r.daily_headroom?.limit}
        tone="up"
      />
    ),
  },
  {
    key: "target_progress",
    header: "Target progress",
    width: 18,
    // Null on qualified accounts.
    render: (r) => (
      <Progress
        used={r.target_progress?.profit}
        total={r.target_progress?.target}
        tone="up"
      />
    ),
  },
  {
    key: "start_date",
    header: "Start Date",
    width: 10,
    value: (r) => startDate(r.start_date),
    sortValue: (r) => {
      const parsed = Date.parse(r.start_date ?? "");
      return Number.isFinite(parsed) ? parsed : null;
    },
    sortable: true,
  },
  // The plan the account was bought on ("Alpha Pro 8%") — TI review E2: this
  // column showed the account state, the same thing Stage shows.
  {
    key: "plan_type",
    header: "Account Type",
    width: 11,
    value: (r) => t(formatPlanName(r.plan_type)),
  },
  // The account state, as a chip: "Phase 1" while evaluating (the API sends
  // "Phase 01"), "Qualified" once through. Every chip in the column is one
  // width (see `.dash-chip-cell`), so the pills line up.
  {
    key: "stage",
    header: "Stage",
    width: 9,
    className: "dash-chip-cell",
    minWidth: CHIP_CELL_MIN,
    render: (r) => {
      const stage = accountStateLabel(phaseLabel(r.stage));
      return stage ? <StatusChip>{stage}</StatusChip> : NA;
    },
  },
  {
    key: "account_size",
    header: "Size",
    width: 7,
    value: (r) => formatAccountSize(r.account_size),
    sortValue: (r) => {
      const n = Number(r.account_size);
      return Number.isFinite(n) ? n : null;
    },
    sortable: true,
  },
  {
    key: "progress",
    header: "Status",
    width: 10,
    className: "dash-chip-cell",
    minWidth: CHIP_CELL_MIN,
    // `progress` as the API sends it, as the current dashboard's Result column
    // does — a disqualified account reads its own outcome ("Time Expired",
    // "Failed") there, not a "Disqualified" of our own.
    render: (r) => (r.progress ? <StatusChip>{r.progress}</StatusChip> : NA),
  },
  {
    key: "action",
    header: "Action",
    width: 7,
    render: (r) => (
      <Button
        variant="pill"
        paddingInline={16}
        iconPlacement="end"
        icon={<span className="icon" style={icon("arrow-right")} />}
        onClick={() => openAccount(r)}
      >
        {t("Open")}
      </Button>
    ),
  },
];

export default function Dashboard() {
  const [params, setParams] = useState({
    accountFilter: "all",
    accountType: "all",
    accountSize: "all",
    search: "",
    series: "Profit",
    range: "1M",
    page: 1,
    pageSize: ROWS_PER_PAGE,
  });

  /** Any change goes back to the first page — unless it is the page itself. */
  const setParam = (key, value) =>
    setParams((prev) => ({ ...prev, page: 1, [key]: value }));

  /** UI state → the endpoint's own field names. */
  const accountQuery = useMemo(
    () => ({
      page: params.page,
      page_size: params.pageSize,
      is_Active: IS_ACTIVE[params.accountFilter],
      account_type: params.accountType !== 'all' ? params.accountType : undefined,
      account_size: params.accountSize !== 'all' ? params.accountSize : undefined,
      // Empty is dropped from the request rather than sent as `?search=`.
      search: params.search || undefined,
    }),
    [
      params.accountFilter,
      params.accountType,
      params.accountSize,
      params.search,
      params.page,
      params.pageSize,
    ],
  );

  const onSearch = useMemo(
    () =>
      debounce(
        (value) => setParams((prev) => ({ ...prev, page: 1, search: value.trim() })),
        SEARCH_DELAY,
      ),
    [],
  );

  const {
    data,
    isFetching,
    isLoading: isAccountsLoading,
    isError,
    error,
    refetch,
  } = useGetAccountsQuery(accountQuery);
  /** The summary row only. */
  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    error: summaryError,
    refetch: refetchSummary,
  } = useGetDashboardStatsQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const picked = useSelector(selectPickedAccount);

  /** The profile card's "Public Page" — the shell's share sheet, verbatim. */
  const [isShareOpen, setIsShareOpen] = useState(false);

  const handleGoToAccountMetrics = (targetLoginId) => {
    if (!targetLoginId) return;
    const matchedAccount = accounts.find((a) => String(a.login_id) === String(targetLoginId));
    const targetPlatform = matchedAccount?.platform ?? ruleLimit?.platform ?? null;
    dispatch(selectAccount({ loginId: targetLoginId, platform: targetPlatform }));
    navigate(
      `/account-metrics?login=${encodeURIComponent(targetLoginId)}` +
      (targetPlatform ? `&platform=${encodeURIComponent(targetPlatform)}` : ''),
    );
  };

  /** Open → Account Metrics for that account, carried in the query string. */
  const columns = useMemo(
    () =>
      buildColumns((account) => {
        dispatch(
          selectAccount({ loginId: account.login_id, platform: account.platform ?? null }),
        );
        navigate(
          `/account-metrics?login=${encodeURIComponent(account.login_id)}` +
          (account.platform ? `&platform=${encodeURIComponent(account.platform)}` : ''),
        );
      }),
    [dispatch, navigate],
  );

  /** The tabs are 1D/1W/1M/1Y/ALL — the exact values the API takes, lowercased. */
  const chart = useGetDashboardChartQuery({
    range: params.range.toLowerCase(),
  });

  const isChartLoading = chart.isLoading || chart.isFetching;

  const points = useMemo(() => toSeries(chart.data, params.series), [chart.data, params.series]);
  const intraday = isIntraday(points);

  /** Last value on the curve, and how far it travelled across the window. */
  const latest = points.length ? points[points.length - 1].y : null;
  const change = points.length > 1 ? latest - points[0].y : null;
  // The same window's balance curve — the base a profit percentage is read on.
  const balancePoints = useMemo(() => toSeries(chart.data, "Balance"), [chart.data]);
  const changePct = (() => {
    if (change == null || !points[0]) return null;
    // Profit is a running P/L: a share of its own first value is meaningless
    // (it can be 0, tiny, or flip sign) and printed "+0.00%" beside a loss
    // (TI review N99). Profit is measured on the balance it was made on;
    // Balance on its own starting value, as before.
    const base = Math.abs(params.series === "Profit" ? balancePoints[0]?.y ?? 0 : points[0].y);
    if (!base) return null;
    return (change / base) * 100;
  })();

  const chartNote = chart.isError
    ? params.series === "Profit"
      ? t('Could not load the profit curve ({{status}}).', { status: chart.error?.status ?? 'error' })
      : t('Could not load the balance curve ({{status}}).', { status: chart.error?.status ?? 'error' })
    : !isChartLoading && points.length === 0
      ? params.series === "Profit"
        ? t('No profit data for this range.')
        : t('No balance data for this range.')
      : null;
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * The page as the endpoint sent it, and deliberately not re-filtered on
   * `is_active`. `?is_Active=true` does answer with rows whose own flag is
   * false — but it is also what counted them, so dropping them here cannot
   * put the two back in agreement: the page renders short (7 rows) under a
   * pager still dividing the server's own total (`1–10 of 25`), and a page
   * where the server disagrees on all ten would come up empty below a pager
   * insisting there are ten. Faithful to the response is the only version the
   * table can be internally consistent about; the disagreement belongs to
   * `/v2/account-metrics/accounts/`, which is where it can be fixed.
   */
  const accounts = useMemo(() => data?.accounts?.results ?? [], [data]);
  /** Server total across every page — what the pager divides into pages. */
  const accountCount = data?.accounts?.count ?? accounts.length;

  /**
   * A trader with no account at all — not a search or filter that came back
   * empty. The table would be a single "no accounts" line, so the card says
   * what to do next instead.
   */
  const hasNoAccounts =
    Boolean(data) &&
    !isError &&
    accountCount === 0 &&
    !params.search &&
    params.accountFilter === "all" &&
    params.accountType === "all" &&
    params.accountSize === "all";

  /**
   * Which account "Public Page" shares. The sheet is per-account and this card
   * is about the trader, so it follows the picker the same way the shell's own
   * Public Page button does — no `?login=` to read first, the Dashboard has
   * none of its own.
   */
  const shareAccountId = picked.loginId ?? accounts[0]?.login_id ?? null;
  const trader = summary?.trader_badge;

  const fundedBalance = summary?.funded_balance;
  const payouts = summary?.lifetime_payouts;
  const ruleLimit = summary?.closest_rule_limit;
  const fundedCount = fundedBalance?.account_count;
  const cleared = payouts?.cleared_count;

  /** The Figma row (1:7925), from `/v2/dashboard/`. */
  const stats = [
    {
      icon: "stat-balance",
      label: "Qualified Balance",
      value: usd(fundedBalance?.amount),
      note:
        fundedCount == null
          ? NA
          : fundedCount === 1
            ? t("Across {{count}} qualified account", { count: fundedCount })
            : t("Across {{count}} qualified accounts", { count: fundedCount }),
      // No delta in the response; badge equity is one account's, not this total's.
      accentTone: "soft",
    },
    {
      icon: "stat-payouts",
      label: "Lifetime Payouts",
      value: usd(payouts?.amount),
      note:
        cleared == null
          ? NA
          : cleared === 1
            ? t("{{count}} payout cleared ,", { count: cleared })
            : t("{{count}} payouts cleared ,", { count: cleared }),
      accent: nextPayout(payouts?.next_payout_in_days),
      accentTone: "soft",
    },
    {
      icon: "stat-rule",
      label: "Closest Rule Limit",
      // `remaining` is the headroom left, which is what "$8,760 left" draws.
      value: usd(ruleLimit?.remaining),
      // The response says which account, not which rule — so no rule name.
      note: ruleLimit?.login_id ? (
        <span className="dash-stat__account">
          <button
            type="button"
            className="dash-stat__login-link"
            onClick={(e) => {
              e.stopPropagation();
              handleGoToAccountMetrics(ruleLimit.login_id);
            }}
            title={t("View Account Metrics for {{login}}", { login: formatLoginId(ruleLimit.login_id) })}
          >
            {formatLoginId(ruleLimit.login_id)}
          </button>

          {/* The whole card navigates, so the copy has to stop the click
              getting there — otherwise taking the number also leaves the page
              it was on. Same control and same toast as the accounts table. */}
          <span
            className="dash-stat__copy"
            onClick={(e) => e.stopPropagation()}
          >
            <CopyButton value={ruleLimit.login_id} label={t("Login ID")} />
          </span>

          <span>· {t("{{amount}} limit", { amount: usd(ruleLimit.limit) })}</span>
          {/* How much of that limit is spent — what the account is ranked by. */}
          {Number.isFinite(Number(ruleLimit.used_pct)) && (
            <span>· {t("{{pct}}% used", { pct: +Number(ruleLimit.used_pct).toFixed(1) })}</span>
          )}
        </span>
      ) : (
        NA
      ),
      accent: resetsIn(ruleLimit?.resets_in_seconds),
      accentTone: "warn",
      stacked: true,
      onClick: ruleLimit?.login_id ? () => handleGoToAccountMetrics(ruleLimit.login_id) : undefined,
    },
  ];

  /** Whether any summary stat value is too long to comfortably fit 4-across. */
  const hasLongData = stats.some(
    (s) => typeof s.value === 'string' && s.value.length > 10
  );

  return (
    <div className="dashboard">
      {/* Either half failing gets the one banner; Retry asks for both. */}
      {(isError || isSummaryError) && (
        <div className="dash-error" role="alert">
          <span>{authErrorMessage(error ?? summaryError)}</span>
          <Button
            variant="ghost"
            onClick={() => {
              if (isError) refetch();
              if (isSummaryError) refetchSummary();
            }}
          >
            {t("Retry")}
          </Button>
        </div>
      )}

      {/* ---- summary row — 1:7925, /v2/dashboard/ ---- */}
      {isSummaryLoading ? (
        <SummarySkeleton />
      ) : (
        <section className={`dash-summary${hasLongData ? ' is-expanded' : ''}`}>
          <div className="dash-profile">
            <span className="dash-profile__avatar">
              {trader?.photo ? (
                <img
                  className="dash-profile__photo"
                  src={trader.photo}
                  alt=""
                />
              ) : (
                <span
                  className="icon dash-profile__mark"
                  style={icon('brand-mark')}
                />
              )}
            </span>
            <div className="dash-profile__foot">
              <div className="dash-profile__id">
                <span className="dash-profile__name">{trader?.name ?? NA}</span>
                <span className="dash-profile__email">
                  {trader?.status ? t('{{status}} trader', { status: t(trader.status) }) : ''}
                </span>
              </div>
              <div className="dash-profile__actions">
                <button
                  type="button"
                  className="dash-profile__btn"
                  onClick={() => navigate('/profile')}
                >
                  {t('View All Details')}
                </button>
                <button
                  type="button"
                  className="dash-profile__btn is-light"
                  onClick={() => setIsShareOpen(true)}
                  disabled={!shareAccountId}
                >
                  {t('Public Page')}
                </button>
              </div>
            </div>
          </div>

          {stats.map((s) => (
            <div
              className={`dash-stat${s.onClick ? ' is-clickable' : ''}`}
              key={s.label}
              onClick={s.onClick}
              role={s.onClick ? 'button' : undefined}
              tabIndex={s.onClick ? 0 : undefined}
              onKeyDown={
                s.onClick
                  ? (e) => {
                    // Only when the card itself has focus. The controls inside
                    // it are buttons that answer Enter and Space on their own,
                    // and a keypress on one bubbles here as well — so copying
                    // the login ID by keyboard would also navigate away.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      s.onClick();
                    }
                  }
                  : undefined
              }
            >
              <div className="dash-stat__head">
                <span className="icon dash-stat__icon" style={icon(s.icon)} />
                <span className="dash-stat__label">{t(s.label)}</span>
              </div>
              <div className="dash-stat__body">
                <div className="dash-stat__value">
                  <span className="dash-stat__number">{s.value}</span>
                </div>
                <p
                  className={`dash-stat__note${s.stacked ? ' is-stacked' : ''}`}
                >
                  <span>{s.note}</span>
                  <span className={`is-${s.accentTone}`}>{s.accent}</span>
                </p>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ---- accounts — 1:8012, /v2/account-metrics/accounts/ ---- */}
      {hasNoAccounts ? (
        <section className="dash-start">
          <span className="icon dash-start__icon" style={icon("assess-target")} />
          <div className="dash-start__copy">
            <h2 className="dash-start__title">{t("You don't have an account yet")}</h2>
            <p className="dash-start__text">
              {t("Start your first challenge to begin trading with Alpha Capital.")}
            </p>
          </div>
          <Button
            variant="submit"
            paddingInline={18}
            iconPlacement="end"
            icon={<span className="icon" style={icon("arrow-right")} />}
            onClick={() => navigate("/trading-assessment")}
          >
            {t("Start Challenge")}
          </Button>
        </section>
      ) : (
        <DataTable
          title="Accounts"
          // "0" is a claim the table can't make before the first response.
          count={isAccountsLoading ? "—" : accountCount}
          columns={columns}
          rows={accounts}
          getRowId={(r) => r.id}
          minWidth={1080}
          // Server-side: `total` hands paging to the API, so `rows` is one page.
          total={accountCount}
          icon={"d-table"}
          page={params.page}
          /* The second argument is the size the pager just switched to. Reading
             it is what lets the selector work at all on a server-side table —
             without it the request would keep asking for the old page size. */
          onPageChange={(page, size) =>
            setParams((prev) => ({ ...prev, page, pageSize: size ?? prev.pageSize }))
          }
          /* Our own state, not the server's echo: the echo is still the previous
             size while the new request is in flight, which would snap the
             selector back for a moment on every change. */
          pageSize={params.pageSize}
          pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
          loading={isFetching}
          emptyMessage={
            isError
              ? "Accounts could not be loaded"
              : params.search
                ? t("No accounts match “{{search}}”", { search: params.search })
                : params.accountFilter !== "all" ||
                  params.accountType !== "all" ||
                  params.accountSize !== "all"
                  ? "No accounts match the selected filters"
                  : "No accounts on this profile yet"
          }
          searchPlaceholder="Search accounts"
          onSearch={onSearch}
          filters={[
            {
              label: "Status",
              options: ACCOUNT_FILTERS,
              value: params.accountFilter,
              onChange: (v) => setParam("accountFilter", v),
            },
            {
              label: "Account Type",
              options: ACCOUNT_TYPE_FILTERS,
              value: params.accountType,
              onChange: (v) => setParam("accountType", v),
            },
            {
              label: "Account Size",
              options: ACCOUNT_SIZE_FILTERS,
              value: params.accountSize,
              onChange: (v) => setParam("accountSize", v),
            },
          ]}
          collapsedRows={COLLAPSED_ROWS}
          isExpanded={isExpanded}
          pagination={isExpanded}
          footer={
            accountCount > COLLAPSED_ROWS && (
              <div
                className={`dash-accounts__footer ${!isExpanded ? "is-collapsed" : ""}`}
              >
                <Button
                  variant="pill"
                  iconPlacement="end"
                  icon={
                    <span
                      className="icon"
                      style={{
                        ...icon("chevron-down-md"),
                        transform: isExpanded ? "rotate(180deg)" : "none",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  }
                  onClick={() => {
                    // Collapsing past page 1 would leave the card showing a
                    // mid-list slice with no pager to explain it.
                    if (isExpanded) setParam("page", 1);
                    setIsExpanded(!isExpanded);
                  }}
                >
                  {isExpanded ? t("Show Less") : t("View all {{count}}", { count: accountCount })}
                </Button>
              </div>
            )
          }
        />
      )}

      {/* ---- chart + insights — 1:8148 ---- */}
      <section className="dash-bottom">
        {chart.isLoading ? (
          <ChartSkeleton />
        ) : (
          <div className="dash-chart">
            <header className="dash-chart__head">
              <div className="dash-chart__left">
                <span className="dash-chart__title">
                  <span
                    className="icon dash-chart__icon"
                    style={icon("chart-profit")}
                  />
                  {t(params.series)}
                </span>
                <SegmentedTabs
                  radius="track"
                  options={["Profit", "Balance"]}
                  value={params.series}
                  onChange={(v) => setParam("series", v)}
                />
              </div>
              <SegmentedTabs
                radius="track"
                options={["1D", "1W", "1M", "1Y", "ALL"]}
                value={params.range}
                onChange={(v) => setParam("range", v)}
              />
            </header>

            <div className="dash-chart__summary">
              {isChartLoading ? (
                <>
                  <Skeleton w="164px" h={34} />
                  <Skeleton w="220px" h={14} />
                </>
              ) : (
                <>
                  <div className="dash-chart__figures">
                    <span className="dash-chart__value">{usd(latest, { decimals: 2 })}</span>
                    {change != null && (
                      <span className={`dash-chart__delta${change < 0 ? ' is-down' : ''}`}>
                        {signedNumber(change)}
                        {changePct != null && ` (${formatPct(changePct)})`}
                      </span>
                    )}
                  </div>
                  <span className="dash-chart__caption">
                    {params.series === "Profit"
                      ? t('Total profit across all accounts')
                      : t('Total balance across all accounts')}
                  </span>
                </>
              )}
            </div>

            <LineChart
              className="dash-chart__plot"
              data={points}
              lineColor={SERIES[params.series].color}
              gradientFrom={SERIES[params.series].color}
              height={224}
              loading={isChartLoading}
              emptyMessage={chartNote ?? undefined}
              xAxisFormatter={intraday ? (v) => CLOCK.format(new Date(v)) : undefined}
              tooltipFormatter={
                intraday
                  ? (p) => `${formatAxisDate(p.x)} ${CLOCK.format(new Date(p.x))} ${formatCurrency(p.y)}`
                  : undefined
              }
            />
          </div>
        )}

        {/* ---- Latest Insights — 1:8233 — static slides, nothing to wait for ---- */}
        <DashboardSlider />
      </section>

      {/* Page level, outside the summary row: that row is swapped for its
          skeleton whenever the summary refetches, which would close the sheet. */}
      <ShareDialog
        open={isShareOpen}
        accountId={shareAccountId}
        account={accounts.find((a) => a.login_id === shareAccountId)}
        onClose={() => setIsShareOpen(false)}
      />
    </div>
  );
}
